/**
 * Lightweight markdown text helpers.
 *
 * These deliberately avoid importing the renderer stack (marked, DOMPurify,
 * highlight.js, and KaTeX) so landing-page and streaming coordination code can
 * run before the full MarkdownRenderer chunk is requested.
 */

/**
 * Convert model tool-call XML into safe, renderable markdown.
 * Non-display tool calls are removed from the user-facing response.
 */
export function preprocessToolCalls(content: string): string {
  if (!content || !content.includes('<tool_call')) return content;

  let text = content;

  const inferLanguageFromPath = (path: string): string => {
    const trimmed = (path || '').trim();
    if (!trimmed) return 'text';
    const filename = trimmed.split(/[\\/]/).pop() ?? '';
    const ext = filename.includes('.') ? filename.split('.').pop()?.toLowerCase() ?? '' : '';
    const extToLang: Record<string, string> = {
      py: 'python',
      js: 'javascript',
      ts: 'typescript',
      tsx: 'tsx',
      jsx: 'jsx',
      html: 'html',
      css: 'css',
      scss: 'scss',
      less: 'less',
      json: 'json',
      md: 'markdown',
      sh: 'bash',
      zsh: 'zsh',
      yml: 'yaml',
      yaml: 'yaml',
      sql: 'sql',
      rs: 'rust',
      go: 'go',
      java: 'java',
      c: 'c',
      cpp: 'cpp',
      h: 'c',
      hpp: 'cpp',
      txt: 'text',
    };
    return extToLang[ext] ?? 'text';
  };

  // code_display (complete) → fenced code block
  // Handles proper </tool_call>, malformed <tool_call> closing, and no closing tag.
  text = text.replace(
    /<tool_call\s+name="code_display"[^>]*>\s*<language>([^<]*)<\/language>\s*<code>([\s\S]*?)<\/code>(?:\s*<execute>[^<]*<\/execute>)?(?:\s*<\/?tool_call\s*>)?/gi,
    (_, lang: string, code: string) =>
      '\n```' + (lang || 'text') + '\n' + code.trim() + '\n```\n'
  );

  // In-progress code_display (no </code> yet, genuinely still streaming).
  text = text.replace(
    /<tool_call\s+name="code_display"[^>]*>\s*<language>([^<]*)<\/language>\s*<code>([\s\S]*)$/gi,
    (_match: string, lang: string, rest: string) => {
      const codeEndIdx = rest.indexOf('</code>');
      if (codeEndIdx >= 0) {
        const code = rest.substring(0, codeEndIdx);
        const after = rest.substring(codeEndIdx + 7);
        return (
          '\n```' + (lang || 'text') + '\n' + code.trim() + '\n```\n' +
          after.replace(/^\s*(?:<\/?tool_call[^>]*>)?\s*/, '')
        );
      }
      return '\n```' + (lang || 'text') + '\n' + rest.trim() + '\n';
    }
  );

  // write_file (complete) → fenced code block with filename metadata.
  text = text.replace(
    /<tool_call\s+name="write_file"[^>]*>([\s\S]*?)<\/tool_call>/gi,
    (_match: string, body: string) => {
      const pathMatch = body.match(/<path>([\s\S]*?)<\/path>/i);
      const contentMatch = body.match(/<content>([\s\S]*?)<\/content>/i);
      const code = (contentMatch?.[1] ?? '').trim();
      if (!code) return '';

      const rawPath = (pathMatch?.[1] ?? '').trim();
      const filename = rawPath ? rawPath.split(/[\\/]/).pop() ?? '' : '';
      const language = inferLanguageFromPath(rawPath);
      const safeFilename = filename.replace(/[^A-Za-z0-9._-]/g, '_');
      const header = safeFilename ? `${language}:${safeFilename}` : language;

      return '\n```' + header + '\n' + code + '\n```\n';
    }
  );

  // Strip other complete tool_call blocks (calculator, web_search, etc.).
  text = text.replace(/<tool_call[^>]*>[\s\S]*?<\/tool_call>/gi, '');

  // Strip orphaned non-code tool_call tags and their immediate XML children.
  text = text.replace(
    /<tool_call\s+name="(?!code_display|write_file)[^"]*"[^>]*>(?:\s*<[a-z_]+>[^<]*<\/[a-z_]+>)*\s*/gi,
    ''
  );

  text = text.replace(/<\/?\s*tool_call[^>]*>/gi, '');

  // Strip bare tool-name outputs when the model omits the wrapper.
  const bareToolNames = [
    'calculator', 'code_display', 'document_search', 'web_search', 'fact_check',
    'save_memory', 'recall_memory', 'search_memory', 'update_memory', 'forget_memory',
    'read_file', 'write_file', 'list_directory', 'search_codebase', 'run_command',
  ];
  const bareToolPattern = new RegExp(
    `\\b(${bareToolNames.join('|')})\\b\\s*(?:<[a-z_]+>[\\s\\S]*?(?:</[a-z_]+>\\s*)*)?$`,
    'gi'
  );
  return text.replace(bareToolPattern, '').trim();
}

/** Split streaming text into completed markdown, prose tail, and an open code block. */
export function splitAtCompletedBlocks(text: string): {
  stableText: string;
  tailText: string;
  streamingBlock: { lang: string; code: string; lines: number } | null;
} {
  const fenceRegex = /```[\s\S]*?```/g;
  let lastEnd = 0;
  let match: RegExpExecArray | null;

  while ((match = fenceRegex.exec(text)) !== null) {
    lastEnd = match.index + match[0].length;
  }

  const stableText = text.substring(0, lastEnd);
  const tail = text.substring(lastEnd);
  const fenceIdx = tail.indexOf('```');
  let streamingBlock: { lang: string; code: string; lines: number } | null = null;

  if (fenceIdx >= 0) {
    const afterFence = tail.substring(fenceIdx + 3);
    const langMatch = afterFence.match(/^(\S*)\s*\n?/);
    const lang = langMatch?.[1] ?? '';
    const codeStart = afterFence.indexOf('\n');
    const code = codeStart >= 0 ? afterFence.substring(codeStart + 1) : '';
    streamingBlock = {
      lang,
      code,
      lines: code.split('\n').length,
    };
  }

  const tailText = fenceIdx >= 0 ? tail.substring(0, fenceIdx) : tail;
  return { stableText, tailText, streamingBlock };
}
